import { Module } from 'vuex';
import { api } from '../../plugins/salut-api';
import { AddressItem } from '../../types';
import {
	District,
	DistrictOptionAllCode,
	districtOptionAllFactory,
	outsideHongKongFactory,
	Region,
	regionOptionAllFactory,
	SubDistrict,
	SubDistrictOptionAllCode,
	subDistrictOptionAllFactory,
} from '../../types/location';
import { LocationState, RootState } from '../types';
import { ProfileGetterKeys } from './profile';

const SET_REGIONS = 'SET_REGIONS';
const SET_DISTRICTS = 'SET_DISTRICTS';
const SET_SUB_DISTRICTS = 'SET_SUB_DISTRICTS';
const SET_AVAILABLE_ADDRESSES = 'SET_AVAILABLE_ADDRESSES';

export const LocationActionKeys = {
	fetchRegions: 'fetchRegions',
	fetchDistricts: 'fetchDistricts',
	fetchSubDistricts: 'fetchSubDistricts',
	fetchAll: 'fetchAll',
	fetchAvailableAddresses: 'fetchAvailableAddresses',
};

export const LocationGetterKeys = {
	regions: 'regions',
	getRegion: 'getRegion',
	getDistrictsByRegion: 'getDistrictsByRegion',
	getDistrict: 'getDistrict',
	getSubDistrictsByDistrict: 'getSubDistrictsByDistrict',
	getSubDistrict: 'getSubDistrict',
	getSubDistricts: 'getSubDistricts',
	subDistrictCount: 'subDistrictCount',
	availableAddresses: 'availableAddresses',
};

const initialState: LocationState = {
	regions: [],
	districts: [],
	subDistricts: [],
	availableAddresses: [],
};

const store: Module<LocationState, RootState> = {
	namespaced: true,

	state: { ...initialState },

	mutations: {
		[SET_REGIONS](state, regions: Region[]) {
			if (!regions) throw new Error('Missing regions');
			state.regions = regions;
		},

		[SET_DISTRICTS](state, districts: District[]) {
			state.districts = districts;
		},

		[SET_SUB_DISTRICTS](state, subDistricts: SubDistrict[]) {
			state.subDistricts = subDistricts;
		},

		[SET_AVAILABLE_ADDRESSES](state, addresses: AddressItem[]) {
			state.availableAddresses = addresses;
		},
	},

	actions: {
		async [LocationActionKeys.fetchRegions]({ state, commit }) {
			if (state.regions.length)
				// already loaded. No need to reload as this data is very static
				return;
			const regions = await api.fetchRegions();
			commit(SET_REGIONS, [
				...regions,
				regionOptionAllFactory(),
				outsideHongKongFactory(),
			]);
		},

		async [LocationActionKeys.fetchDistricts]({ state, commit }) {
			if (state.districts.length)
				// already loaded. No need to reload as this data is very static
				return;
			const districts = await api.fetchDistricts();
			commit(SET_DISTRICTS, districts);
		},

		async [LocationActionKeys.fetchSubDistricts]({ state, commit }) {
			if (state.subDistricts.length)
				// already loaded. No need to reload as this data is very static
				return;
			const subDistricts = await api.fetchSubDistricts();
			commit(SET_SUB_DISTRICTS, subDistricts);
		},

		[LocationActionKeys.fetchAll]({ dispatch }) {
			dispatch(LocationActionKeys.fetchRegions);
			dispatch(LocationActionKeys.fetchDistricts);
			dispatch(LocationActionKeys.fetchSubDistricts);
		},

		async [LocationActionKeys.fetchAvailableAddresses]({
			commit,
			rootGetters,
		}) {
			const ngoId = rootGetters[`profile/${ProfileGetterKeys.basic}`].ngoID;
			if (!ngoId) return console.error('Missing NGO Id');

			const centers = await api.fetchCenters(ngoId);

			// get unique addresses from NGO and centers
			const addressItemSet = new Set<string>();
			for (const center of centers) {
				if (
					/**
					 * only take addresses with all required fields filled in
					 */
					center.regionCode &&
					center.districtCode &&
					center.subDistrictCode &&
					Object.values(center.addressLine).some(v => !!v)
				) {
					addressItemSet.add(
						JSON.stringify({
							isVirtual: center.isVirtual,
							addressLine: center.addressLine,
							mapLatLong: center.mapLatLong,
							regionCode: center.regionCode,
							districtCode: center.districtCode,
							subDistrictCode: center.subDistrictCode,
						}),
					);
				}
			}
			const addressItems = Array.from(addressItemSet).map(addressString =>
				JSON.parse(addressString),
			);
			commit(SET_AVAILABLE_ADDRESSES, addressItems);
		},
	},

	getters: {
		[LocationGetterKeys.regions]: state => state.regions,

		[LocationGetterKeys.getRegion]: state => (regionCode: string) =>
			state.regions.find(region => region.code === regionCode),

		[LocationGetterKeys.getDistrictsByRegion]: state => (regionCode: string) =>
			state.districts.filter(district => district.regionCode === regionCode),

		[LocationGetterKeys.getDistrict]: state =>
			// TODO: districtCode is unique across Hong Kong, but will it still be true if we expand?
			(districtCode: string) => {
				if (districtCode === DistrictOptionAllCode) {
					return districtOptionAllFactory();
				}
				return state.districts.find(district => district.code === districtCode);
			},

		[LocationGetterKeys.getSubDistricts]: state => state.subDistricts,

		[LocationGetterKeys.getSubDistrictsByDistrict]: state => (
			districtCode: string,
		) =>
			state.subDistricts.filter(
				subDistrict => subDistrict.districtCode === districtCode,
			),

		[LocationGetterKeys.getSubDistrict]: state =>
			// TODO: subDistrictCode is unique across Hong Kong, but will it still be true if we expand?
			(subDistrictCode: string) => {
				if (subDistrictCode === SubDistrictOptionAllCode) {
					return subDistrictOptionAllFactory();
				}
				return state.subDistricts.find(
					subDistrict => subDistrict.code === subDistrictCode,
				);
			},

		[LocationGetterKeys.subDistrictCount]: state => state.subDistricts.length,

		[LocationGetterKeys.availableAddresses]: state =>
			state.availableAddresses || [],
	},
};

export default store;
